import React, { useState, useRef } from 'react';
import { db, storage } from '../firebaseConfig'; // Import storage
import { collection, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, writeBatch, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"; // Import storage functions
import { usePromotions } from '../hooks/useFirestoreData';
import './AdminPromotions.css'; // Import the new CSS file

function AdminPromotions() {
    const [error, setError] = useState(null);
    const { promotions, loadingPromotions } = usePromotions(setError);
    const [editingPromotion, setEditingPromotion] = useState(null);
    const graphicPieceInputRef = useRef(null); // Ref for the main graphic piece input
    const [showForm, setShowForm] = useState(false); // State to control form visibility
    const [formState, setFormState] = useState({
        title: '',
        description: '',
        graphicPieceUrl: '',
        choiceText: '',
        options: [],
        dates: [],
    });
    const [currentDate, setCurrentDate] = useState('');

    const handleAddPromotionClick = () => {
        setEditingPromotion(null);
        setFormState({
            title: '',
            description: '',
            graphicPieceUrl: '',
            choiceText: '',
            options: [],
            dates: [],
        });
        if (graphicPieceInputRef.current) {
            graphicPieceInputRef.current.value = '';
        }
        setShowForm(true);
    };

    const handleImageUpload = async (file, promotionId) => {
        const storageRef = ref(storage, `promotions/${promotionId}/${file.name}`);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        return downloadURL;
    };

    const handleInputChange = async (e) => {
        const { name, value, files } = e.target;
        if (name === 'graphicPieceUrl' && files && files[0]) {
            const file = files[0];
            // For new promotions, we might need a temporary ID or handle this after creation
            const promotionId = editingPromotion ? editingPromotion.id : new Date().getTime().toString();
            const downloadURL = await handleImageUpload(file, promotionId);
            setFormState(prevState => ({ ...prevState, graphicPieceUrl: downloadURL }));
        } else {
            setFormState(prevState => ({ ...prevState, [name]: value }));
        }
    };

    const handleOptionChange = async (index, e) => {
        const { name, value, files } = e.target;
        const options = [...formState.options];

        if (name === 'imageUrl' && files) {
            const promotionId = editingPromotion ? editingPromotion.id : formState.title.replace(/\s+/g, '_');
            const uploadedImageUrls = [];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const downloadURL = await handleImageUpload(file, `${promotionId}_option_${index}_${file.name}`);
                uploadedImageUrls.push(downloadURL);
            }
            options[index][name] = [...options[index][name], ...uploadedImageUrls]; // Append new URLs
        } else if (name === 'includedServices') {
            options[index][name] = value.split(',').map(s => s.trim());
        } else {
            options[index][name] = value;
        }
        setFormState(prevState => ({ ...prevState, options }));
    };

    const handleRemoveOptionImage = (optionIndex, imageIndex) => {
        const options = [...formState.options];
        options[optionIndex].imageUrl.splice(imageIndex, 1);
        setFormState(prevState => ({ ...prevState, options }));
    };

    const addOption = () => {
        setFormState(prevState => ({
            ...prevState,
            options: [...prevState.options, {
                id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Assign a more unique ID
                name: '',
                capacityText: '',
                price: 0,
                stock: 0, // New stock field
                modalTitle: '',
                modalDescription: '',
                includedServices: [],
                imageUrl: [] // Changed to array
            }]
        }));
    };

    const removeOption = (index) => {
        const options = [...formState.options];
        options.splice(index, 1);
        setFormState(prevState => ({ ...prevState, options }));
    };

    const handleAddDate = () => {
        if (currentDate) {
            setFormState(prev => ({
                ...prev,
                dates: [...(prev.dates || []), currentDate]
            }));
            setCurrentDate('');
        }
    };

    const handleRemoveDate = (dateToRemove) => {
        setFormState(prev => ({
            ...prev,
            dates: prev.dates.filter(date => date !== dateToRemove)
        }));
    };

    const handleSavePromotion = async () => {
        if (!formState.title) {
            alert('Please enter a title for the promotion.');
            return;
        }

        try {
            if (editingPromotion) {
                const promotionRef = doc(db, 'promotions', editingPromotion.id);
                await updateDoc(promotionRef, formState);
                setEditingPromotion(null);
            } else {
                await addDoc(collection(db, 'promotions'), {
                    ...formState,
                    createdAt: serverTimestamp()
                });
            }
            setFormState({
                title: '',
                description: '',
                graphicPieceUrl: '',
                choiceText: '',
                options: [],
                dates: [],
            });
            if (graphicPieceInputRef.current) {
                graphicPieceInputRef.current.value = '';
            }
            setShowForm(false); // Hide the form after saving
        } catch (e) {
            console.error("Error saving promotion: ", e);
            setError("Error saving promotion.");
        }
    };

    const handleEditPromotion = (promotion) => {
        setEditingPromotion(promotion);
        // Ensure stock is initialized and ID is present for old promotions if not present
        const optionsWithStockAndId = promotion.options.map(option => ({
            ...option,
            id: option.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Assign ID if missing, or generate a more unique one
            stock: option.stock !== undefined ? option.stock : 0,
            imageUrl: Array.isArray(option.imageUrl) ? option.imageUrl : (option.imageUrl ? [option.imageUrl] : []) // Ensure imageUrl is an array
        }));
        setFormState({ ...promotion, options: optionsWithStockAndId, dates: promotion.dates || [] });
        setShowForm(true); // Show the form when editing
    };

    const handleDeletePromotion = async (id) => {
        if (window.confirm('Are you sure you want to delete this promotion?')) {
            try {
                await deleteDoc(doc(db, 'promotions', id));
            } catch (e) {
                console.error("Error deleting promotion: ", e);
                setError("Error deleting promotion.");
            }
        }
    };

    const handleSetPopupPromotion = async (promotionId) => {
        const batch = writeBatch(db);
        
        // Reset any existing popup promotion
        const q = query(collection(db, "promotions"), where("isPopup", "==", true));
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
            batch.update(doc.ref, { isPopup: false });
        });

        // Set the new popup promotion
        const promotionRef = doc(db, 'promotions', promotionId);
        batch.update(promotionRef, { isPopup: true });

        try {
            await batch.commit();
            alert('Popup promotion updated successfully!');
        } catch (error) {
            console.error("Error setting popup promotion: ", error);
            setError("Error setting popup promotion.");
        }
    };

    const handleDisablePopup = async () => {
        const batch = writeBatch(db);
        const q = query(collection(db, "promotions"), where("isPopup", "==", true));
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
            batch.update(doc.ref, { isPopup: false });
        });
        try {
            await batch.commit();
            alert('Popup disabled successfully!');
        } catch (error) {
            console.error("Error disabling popup: ", error);
            setError("Error disabling popup.");
        }
    };

    const activePopup = promotions.find(promo => promo.isPopup);

    if (loadingPromotions) {
        return <p>Loading promotions...</p>;
    }

    if (error) {
        return <p>Error: {error}</p>;
    }

    return (
        <div className="admin-promotions-container">
            <h2>Gestionar Eventos</h2>

            {!showForm && !editingPromotion && (
                <button onClick={handleAddPromotionClick} className="add-new-promotion-btn">Agregar Nuevo Evento</button>
            )}

            {(showForm || editingPromotion) && (
                <div className="promotion-form-section">
                    <h3>{editingPromotion ? 'Editar Evento' : 'Agregar Nuevo Evento'}</h3>
                    <input type="text" name="title" value={formState.title} onChange={handleInputChange} placeholder="Título del Evento" />
                    <textarea name="description" value={formState.description} onChange={handleInputChange} placeholder="Descripción"></textarea>
                    <p>Pieza Gráfica:</p>
                    <input type="file" name="graphicPieceUrl" onChange={handleInputChange} accept="image/*" ref={graphicPieceInputRef} />
                    {formState.graphicPieceUrl && <img src={formState.graphicPieceUrl} alt="Promotion Graphic" style={{ width: '100px', height: 'auto' }} />}
                    <input type="text" name="choiceText" value={formState.choiceText} onChange={handleInputChange} placeholder="Texto de Elección (e.g., 'Elige tu:')" />

                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Fechas:</label>
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                            <input
                                type="date"
                                value={currentDate}
                                onChange={(e) => setCurrentDate(e.target.value)}
                                style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                            />
                            <button
                                type="button"
                                onClick={handleAddDate}
                                style={{ padding: '10px 15px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                            >
                                Agregar Fecha
                            </button>
                        </div>
                        <ul style={{ listStyleType: 'none', padding: 0 }}>
                            {formState.dates?.map(date => (
                                <li key={date} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                                    <span>{date}</span>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveDate(date)}
                                        style={{ padding: '5px 10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                    >
                                        Eliminar
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <h4>Opciones (Tarjetas)</h4>
                    <div className="options-container">
                        {formState.options.map((option, index) => (
                            <div key={index} className="option-card-admin">
                                <h5>Opción {index + 1}</h5>
                                <input type="text" name="name" value={option.name} onChange={(e) => handleOptionChange(index, e)} placeholder="Nombre de la Opción (e.g., Opción 1)" />
                                <input type="text" name="capacityText" value={option.capacityText} onChange={(e) => handleOptionChange(index, e)} placeholder="Texto de Capacidad" />
                                <input type="number" name="price" value={option.price} onChange={(e) => handleOptionChange(index, e)} placeholder="Precio" />
                                <input type="number" name="stock" value={option.stock} onChange={(e) => handleOptionChange(index, e)} placeholder="Inventario" />
                                <input type="text" name="modalTitle" value={option.modalTitle} onChange={(e) => handleOptionChange(index, e)} placeholder="Título del Modal" />
                                <textarea name="modalDescription" value={option.modalDescription} onChange={(e) => handleOptionChange(index, e)} placeholder="Descripción del Modal"></textarea>
                                <input type="text" name="includedServices" value={option.includedServices.join(', ')} onChange={(e) => handleOptionChange(index, e)} placeholder="Servicios Incluidos (separados por comas)" />
                                <p>Imágenes:</p>
                                <input type="file" name="imageUrl" onChange={(e) => handleOptionChange(index, e)} accept="image/*" multiple />
                                <div className="option-image-previews">
                                    {option.imageUrl.map((url, imgIndex) => (
                                        <div key={imgIndex} className="image-preview-item">
                                            <img src={url} alt={`Option ${index} Image ${imgIndex}`} style={{ width: '100px', height: 'auto' }} />
                                            <button type="button" onClick={() => handleRemoveOptionImage(index, imgIndex)} className="remove-image-btn">X</button>
                                        </div>
                                    ))}
                                </div>
                                <button onClick={() => removeOption(index)} className="remove-option-btn">Eliminar Opción</button>
                            </div>
                        ))}
                    </div>
                    <button onClick={addOption} className="add-option-btn">Agregar Opción</button>
                    <hr />
                    <div className="action-buttons">
                        <button onClick={handleSavePromotion}>{editingPromotion ? 'Update Promotion' : 'Save Promotion'}</button>
                        {editingPromotion && <button onClick={() => {
                            setEditingPromotion(null);
                            setFormState({ title: '', description: '', graphicPieceUrl: '', choiceText: '', options: [], dates: [] });
                            if (graphicPieceInputRef.current) {
                                graphicPieceInputRef.current.value = '';
                            }
                            setShowForm(false); // Hide the form
                        }} className="cancel-edit">Cancel Edit</button>}
                    </div>
                </div>
            )}

            <hr />

            <div className="existing-promotions-header">
                <h3>Existing Promotions</h3>
                <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                    {activePopup ? (
                        <p style={{color: 'green', fontWeight: 'bold'}}>Popup Activo: {activePopup.title}</p>
                    ) : (
                        <p style={{color: 'red', fontWeight: 'bold'}}>No hay Popup Activo</p>
                    )}
                    <button onClick={handleDisablePopup} className="disable-popup-btn">Desactivar Popup</button>
                </div>
            </div>
            <div className="existing-promotions-list">
                {promotions.map(promo => (
                    <div key={promo.id} className={`existing-promotion-item ${promo.isPopup ? 'popup-promotion' : ''}`}>
                        <div>
                            <h4>{promo.title} {promo.isPopup && <span>(Popup)</span>}</h4>
                            <p>{promo.description}</p>
                        </div>
                        <div className="actions">
                            <button onClick={() => handleSetPopupPromotion(promo.id)} className="set-popup-btn">Set as Popup</button>
                            <button onClick={() => handleEditPromotion(promo)} className="edit-btn">Edit</button>
                            <button onClick={() => handleDeletePromotion(promo.id)} className="delete-btn">Delete</button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default AdminPromotions;